// Auth context: текущий user + связь с tenant/branch.
// Sprint 11: реальная интеграция с backend (/auth/me, /auth/login, /auth/logout).
// Сессия — httpOnly cookie (см. apps/api auth.controller), фронт хранит только user-объект.

import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { api } from './api';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type UserRole = 'boss' | 'manager' | 'senior_admin' | 'admin' | 'sales_manager' | 'teacher';

/** Иерархия ролей (выше = шире права) — для «эффективной» роли при совмещении. */
const ROLE_RANK: Record<UserRole, number> = {
  boss: 5,
  manager: 4,
  senior_admin: 3,
  admin: 2,
  sales_manager: 1,
  teacher: 0,
};

/** Высшая из основной и второй роли — по ней строится видимость меню. */
export function effectiveRole(role: UserRole, secondary?: UserRole | null): UserRole {
  if (!secondary) return role;
  return ROLE_RANK[secondary] > ROLE_RANK[role] ? secondary : role;
}

/** Ответ backend на GET /auth/me и POST /auth/login (успех). */
export interface CurrentUserResponse {
  user_id: string;
  email: string;
  tenant_id: string;
  tenant_name: string | null;
  employee_id: string | null;
  membership: {
    membership_id: string;
    role: UserRole;
    secondary_role?: UserRole | null;
    branch_ids: string[];
    /**
     * Эффективные действия (union прав основной и второй роли + overrides
     * тенанта из раздела «Доступ»). Источник правды для видимости меню/кнопок.
     */
    actions?: string[];
  };
  mfa_verified: boolean;
  /** true — временный пароль: до смены доступны только auth-endpoints. */
  must_change_password: boolean;
}

/** Возможные не-успешные исходы login (boss + MFA). */
type LoginMfaResult =
  | { mfa_required: true; user_id: string }
  | { mfa_setup_required: true; user_id: string };

export interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  /** Вторая роль при совмещении (педагог/менеджер и т.п.). */
  secondaryRole: UserRole | null;
  tenantId: string;
  tenantName: string | null;
  membershipId: string;
  employeeId: string | null;
  /** branch_ids доступные user'у; пустой массив = «все филиалы» (Boss). */
  branchScopes: string[];
  /**
   * Эффективные действия из /auth/me (роль + совмещение + overrides «Доступа»).
   * UI гейтит меню и кнопки ТОЛЬКО через can() — не хардкодит роли.
   */
  actions: ReadonlySet<string>;
  /** Временный пароль — приложение закрыто до смены пароля. */
  mustChangePassword: boolean;
}

/** Результат signIn для UI: куда вести пользователя дальше. */
export type SignInOutcome =
  | { kind: 'ok'; user: CurrentUser }
  | { kind: 'mfa_required' }
  | { kind: 'mfa_setup_required' };

export interface AuthContextValue {
  status: AuthStatus;
  user: CurrentUser | null;
  signIn: (email: string, password: string) => Promise<SignInOutcome>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Маппинг ответа backend → внутренний user-объект. */
function toCurrentUser(res: CurrentUserResponse): CurrentUser {
  return {
    id: res.user_id,
    email: res.email,
    // Backend пока не отдаёт ФИО — используем email как display name.
    fullName: res.email,
    role: res.membership.role,
    secondaryRole: res.membership.secondary_role ?? null,
    tenantId: res.tenant_id,
    tenantName: res.tenant_name,
    membershipId: res.membership.membership_id,
    employeeId: res.employee_id,
    branchScopes: res.membership.branch_ids,
    actions: new Set(res.membership.actions ?? []),
    // Старые ответы без поля трактуем как false (обычный пароль).
    mustChangePassword: res.must_change_password ?? false,
  };
}

function isMfaResult(value: unknown): value is LoginMfaResult {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('mfa_required' in value || 'mfa_setup_required' in value)
  );
}

export interface AuthProviderProps {
  children: ReactNode;
  // Тестовая инъекция начального состояния — не использовать в проде.
  initialUser?: CurrentUser | null;
  initialStatus?: AuthStatus;
}

export function AuthProvider({
  children,
  initialUser = null,
  initialStatus,
}: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<CurrentUser | null>(initialUser);
  const [status, setStatus] = useState<AuthStatus>(
    initialStatus ?? (initialUser ? 'authenticated' : 'loading'),
  );
  // AuthProvider рендерится ПОД QueryClientProvider (main.tsx → App → Tenant → Auth),
  // поэтому useQueryClient() здесь доступен. Нужен, чтобы при завершении сессии
  // чистить кэш TanStack Query — иначе на общем устройстве следующий логин увидит
  // данные предыдущего пользователя (Fix A, security).
  const queryClient = useQueryClient();

  // Единая точка сброса локального auth-состояния. Любой путь завершения сессии
  // (ручной выход, истечение/отсутствие сессии) проходит здесь, поэтому кэш
  // чистится централизованно — не оставляем «дыры» с данными прошлого юзера.
  const resetSession = useCallback((): void => {
    setUser(null);
    setStatus('unauthenticated');
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const res = await api.get<CurrentUserResponse>('/auth/me');
      setUser(toCurrentUser(res));
      setStatus('authenticated');
    } catch {
      // 401 = нет сессии. Любая другая ошибка тоже трактуется как «не авторизован»,
      // чтобы UI не висел в loading. В обоих случаях сессия считается завершённой —
      // сбрасываем состояние и чистим кэш.
      resetSession();
    }
  }, [resetSession]);

  const signIn = useCallback(async (email: string, password: string): Promise<SignInOutcome> => {
    const res = await api.post<CurrentUserResponse | LoginMfaResult>('/auth/login', {
      email,
      password,
    });

    if (isMfaResult(res)) {
      if ('mfa_setup_required' in res) {
        return { kind: 'mfa_setup_required' };
      }
      return { kind: 'mfa_required' };
    }

    const nextUser = toCurrentUser(res);
    setUser(nextUser);
    setStatus('authenticated');
    return { kind: 'ok', user: nextUser };
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Даже если logout-запрос упал — локально разлогиниваем пользователя.
    }
    // Сброс состояния + очистка кэша (Fix A): на общем устройстве следующий логин
    // не должен видеть данные предыдущего пользователя.
    resetSession();
  }, [resetSession]);

  useEffect(() => {
    // На монтировании один раз проверяем сессию через /auth/me.
    if (status === 'loading') {
      void refresh();
    }
  }, [status, refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, signIn, signOut, refresh }),
    [status, user, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}

export function useCurrentUser(): CurrentUser | null {
  return useAuth().user;
}

/**
 * Текущий пользователь БЕЗ требования провайдера: null, если <AuthProvider>
 * над деревом нет (изолированные тесты форм). Для подсказок и умолчаний,
 * где отсутствие пользователя — не ошибка, а «ничего не знаем».
 */
export function useOptionalCurrentUser(): CurrentUser | null {
  return useContext(AuthContext)?.user ?? null;
}

/**
 * Проверка права текущего пользователя — единственный правильный способ гейтить
 * меню/кнопки в UI. Backend считает union прав основной и второй роли с учётом
 * overrides тенанта (раздел «Доступ»), поэтому фронт НЕ должен дублировать
 * ролевую логику (иначе меню разъезжается с матрицей — до 2026-07-19 так и было).
 */
export function useCan(): (action: string) => boolean {
  const user = useAuth().user;
  return useCallback((action: string) => user?.actions.has(action) ?? false, [user]);
}
